import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { AsyncResource, useAsyncResource } from '../hooks/useAsyncResource';
import { getPnlConfig, getStarGridHealth, getStarGridInfra, listPnlReports } from '../services/starGridService';
import { PnlConfigResponse, PnlReportSummary, StarGridHealth, StarGridInfra } from '../starGridTypes';

/**
 * Generic interface (state + actions) for StarGrid's shared server state, so views depend on
 * this contract rather than on how the data is fetched or cached.
 */
export interface StarGridData {
  /** Persisted PnL reports — shared by the Reports nav section and the report views. */
  pnlReports: AsyncResource<PnlReportSummary[]>;
  /** Global PnL config (available scenarios / dimensions / measures) — cached across views. */
  pnlConfig: AsyncResource<PnlConfigResponse>;
  infra: AsyncResource<StarGridInfra>;
  health: AsyncResource<StarGridHealth>;
  /** AppLayout `tools` (right-side info panel) — content + open state, driven by sub-views. */
  toolsContent: React.ReactNode;
  toolsOpen: boolean;
  setTools: (content: React.ReactNode) => void;
  setToolsOpen: (open: boolean) => void;
}

const StarGridDataContext = createContext<StarGridData | undefined>(undefined);

/**
 * Owns all of StarGrid's shared server state. Mounted once, above the sub-view switch in
 * StarGridPage, so switching between Home / Create Report / Infra does not remount — and
 * therefore does not re-fetch — the underlying resources. Each resource loads lazily on the first
 * sub-view that needs it and stays cached until the user leaves StarGrid or hits Refresh. This is
 * the single place that knows how the data is fetched; views consume it through `useStarGridData`.
 */
export const StarGridDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pnlReports = useAsyncResource<PnlReportSummary[]>(listPnlReports);
  const pnlConfig = useAsyncResource<PnlConfigResponse>(getPnlConfig);
  const infra = useAsyncResource<StarGridInfra>(getStarGridInfra);
  const health = useAsyncResource<StarGridHealth>(getStarGridHealth);

  const [toolsContent, setToolsContent] = useState<React.ReactNode>(null);
  const [toolsOpen, setToolsOpen] = useState<boolean>(false);
  const setTools = useCallback((content: React.ReactNode) => {
    setToolsContent(content);
  }, []);

  const value = useMemo<StarGridData>(
    () => ({ pnlReports, pnlConfig, infra, health, toolsContent, toolsOpen, setTools, setToolsOpen }),
    [pnlReports, pnlConfig, infra, health, toolsContent, toolsOpen, setTools]
  );

  return <StarGridDataContext.Provider value={value}>{children}</StarGridDataContext.Provider>;
};

/** Access StarGrid's shared server state. Must be called within a StarGridDataProvider. */
export function useStarGridData(): StarGridData {
  const context = useContext(StarGridDataContext);
  if (!context) {
    throw new Error('useStarGridData must be used within a StarGridDataProvider.');
  }
  return context;
}
